import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Modal } from 'antd';
import PropTypes from 'prop-types';
import { importUsersFromExcel } from '../../api/usersApi';

const ImportUsersModal = ({ isOpen, onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [importErrors, setImportErrors] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    setFile(null);
    setLoading(false);
    setDone(false);
    setError('');
    setSuccessMessage('');
    setImportErrors([]);
  }, [isOpen]);

  const handleImport = useCallback(async (selectedFile) => {
    if (!selectedFile) {
      setError('Izaberite Excel fajl.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');
    setImportErrors([]);

    const response = await importUsersFromExcel(selectedFile);
    if (!response.ok) {
      setError(response.message);
      setImportErrors(response.data?.errors || []);
      setLoading(false);
      return;
    }

    const createdCount = response.data?.createdCount ?? 0;
    const updatedCount = response.data?.updatedCount ?? 0;
    const skippedCount = response.data?.skippedCount ?? 0;
    const errors = response.data?.errors || [];

    setSuccessMessage(
      `${response.message || 'Pacijenti importovani.'} Kreirano: ${createdCount}. Azurirano: ${updatedCount}. Preskoceno: ${skippedCount}.`
    );
    setImportErrors(errors);
    onImported?.();

    if (!errors.length) {
      setDone(true);
      setTimeout(() => {
        setDone(false);
        onClose();
      }, 1300);
    }

    setLoading(false);
  }, [onClose, onImported]);

  const handleCancel = () => {
    setFile(null);
    setLoading(false);
    setDone(false);
    setError('');
    setSuccessMessage('');
    setImportErrors([]);
    onClose();
  };

  return (
    <Modal
      className="create_user_modal"
      title="Import pacijenata iz Excel-a"
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
    >
      <div className="create_user_modal_form" autoComplete="off">
        <div>
          Izaberite Excel fajl sa pacijentima. Podrzan format fajla je .xlsx.
        </div>
        <div>
          <label
            htmlFor="users-import-file"
            style={{ display: 'block', marginBottom: 6 }}
          >
            Excel fajl (.xlsx)
          </label>
          <input
            id="users-import-file"
            type="file"
            accept=".xlsx"
            disabled={loading || done}
            onChange={(e) => {
              const selectedFile = e.target.files?.[0] || null;
              setFile(selectedFile);
              setDone(false);
              setError('');
              setSuccessMessage('');
              setImportErrors([]);
              handleImport(selectedFile);
            }}
          />
          <div style={{ marginTop: 8, color: '#667085', fontSize: 13 }}>
            {file ? `Izabran fajl: ${file.name}` : 'Nijedan fajl nije izabran.'}
          </div>
        </div>
        {successMessage && (
          <Alert
            title={successMessage}
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        {error && (
          <Alert
            title={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        {!!importErrors.length && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            title={`Greske u importu (${importErrors.length})`}
            description={
              <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                {importErrors.map((item, index) => (
                  <div key={`${item.rowNumber || index}-${item.lbo || ''}`}>
                    Red {item.rowNumber || '-'}
                    {item.lbo ? `, LBO ${item.lbo}` : ''}: {item.message}
                  </div>
                ))}
              </div>
            }
          />
        )}
      </div>
    </Modal>
  );
};

ImportUsersModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onImported: PropTypes.func,
};

export default ImportUsersModal;
